/**
 * HighLevelAPI — Extracted high-level convenience methods from FirebaseIntelligenceService.
 *
 * These methods are thin wrappers over `rawGenerateContent` that handle:
 *   - Prompt preparation & sanitization
 *   - Thinking budget configuration
 *   - Semantic caching
 *   - Response parsing
 *   - Multimodal content assembly
 *
 * Each function takes `IntelligenceContext` as its first argument.
 */

import type { Content, Part, Schema, Tool, WrappedResponse } from '@/shared/types/ai.dto';
type GenerateContentResult = WrappedResponse;
import type { IntelligenceContext } from '../IntelligenceContext';
import type {
    GenerationConfig,
    GenerateContentOptions,
} from '@/shared/types/ai.dto';
import { AppErrorCode, AppException } from '@/shared/types/errors';
import { PromptSanitizer } from '@/services/security/PromptSanitizer';
import { aiCache } from '../IntelligenceResponseCache';
import { logger } from '@/utils/logger';
import type { ChatMessage } from '../types';
import { normalizeDisabledThinkingConfig } from './thinkingConfig';

/**
 * Generate text with optional thinking budget and system instruction.
 */
export async function generateText(
    ctx: IntelligenceContext,
    prompt: string | Part[],
    thinkingBudgetOrModelOrConfig?: number | string | Record<string, unknown>,
    systemInstructionOrConfig?: string | Record<string, unknown>
): Promise<string> {
    await ctx.ensureInitialized();
    let model: string | undefined;
    let config: Record<string, unknown> = {};
    let systemInstruction: string | undefined;

    // Item 250: Sanitize user-provided string prompts before sending to Gemini
    if (typeof prompt === 'string') {
        prompt = PromptSanitizer.sanitizeOrThrow(prompt);
    }

    if (typeof thinkingBudgetOrModelOrConfig === 'string') {
        model = thinkingBudgetOrModelOrConfig;
    }

    const modelName = model || ctx.getModelName();
    const cacheKey = typeof prompt === 'string' ? prompt : JSON.stringify(prompt);

    if (typeof thinkingBudgetOrModelOrConfig === 'number') {
        config.thinkingConfig = {
            thinkingBudget: thinkingBudgetOrModelOrConfig,
            includeThoughts: true
        };
        if (typeof systemInstructionOrConfig === 'string') {
            systemInstruction = systemInstructionOrConfig;
        } else if (typeof systemInstructionOrConfig === 'object' && systemInstructionOrConfig !== null) {
            config = { ...config, ...(systemInstructionOrConfig as Record<string, unknown>) };
            if ('systemInstruction' in config && typeof config.systemInstruction === 'string') {
                systemInstruction = config.systemInstruction;
            }
        }
    } else if (typeof thinkingBudgetOrModelOrConfig === 'string') {
        if (typeof systemInstructionOrConfig === 'string') {
            systemInstruction = systemInstructionOrConfig;
        } else {
            config = (systemInstructionOrConfig as Record<string, unknown>) || {};
            systemInstruction = config && typeof config === 'object' && 'systemInstruction' in config ? (config as { systemInstruction: string }).systemInstruction : undefined;
        }
    } else if (typeof thinkingBudgetOrModelOrConfig === 'object' && thinkingBudgetOrModelOrConfig !== null) {
        config = thinkingBudgetOrModelOrConfig as Record<string, unknown>;
        systemInstruction = typeof systemInstructionOrConfig === 'string' ? systemInstructionOrConfig : undefined;
        if (!systemInstruction && 'systemInstruction' in config && typeof config.systemInstruction === 'string') {
            systemInstruction = config.systemInstruction;
        }
    }

    config = normalizeDisabledThinkingConfig(config);

    // Semantic Cache Check
    const cached = await aiCache.get(cacheKey, modelName, config);
    if (cached) return cached;

    const result = await ctx.rawGenerateContent(
        typeof prompt === 'string' ? prompt : [{ role: 'user', parts: prompt }],
        modelName,
        config,
        systemInstruction
    );

    if (!result?.response) {
        throw new AppException(AppErrorCode.INTERNAL_ERROR, 'Intelligence Service returned an invalid response structure');
    }

    const text = typeof result.response.text === 'function' ? result.response.text() : '';
    await aiCache.set(cacheKey, text, modelName, config);
    return text;
}

/**
 * Generate structured data from a prompt/parts and schema.
 */
export async function generateStructuredData<T>(
    ctx: IntelligenceContext,
    promptOrOptions: string | Part[] | GenerateContentOptions,
    schemaOrConfig?: Schema | Record<string, unknown>,
    thinkingBudgetOrConfig?: number | Record<string, unknown>,
    systemInstruction?: string,
    modelOverride?: string
): Promise<T> {
    await ctx.ensureInitialized();

    let prompt: string | Content[];
    let schema: Schema | undefined;
    let config: GenerationConfig = {};
    let finalSystemInstruction = systemInstruction;
    let modelName = modelOverride || ctx.getModelName();

    if (typeof promptOrOptions === 'object' && !Array.isArray(promptOrOptions) && 'contents' in promptOrOptions) {
        // Options object pattern
        const options = promptOrOptions as GenerateContentOptions;
        prompt = options.contents as Content[];
        config = options.config || {};
        schema = config.responseSchema as Schema;
        finalSystemInstruction = options.systemInstruction;
        modelName = options.model || modelName;
    } else {
        // Positional arguments pattern
        prompt = typeof promptOrOptions === 'string' ? promptOrOptions : [{ role: 'user', parts: promptOrOptions as Part[] }];
        schema = schemaOrConfig as Schema;
        config = {
            responseMimeType: 'application/json',
            responseSchema: schema
        };
        if (typeof thinkingBudgetOrConfig === 'number') {
            config.thinkingConfig = {
                thinkingBudget: thinkingBudgetOrConfig,
                includeThoughts: true
            };
        } else if (typeof thinkingBudgetOrConfig === 'object' && thinkingBudgetOrConfig !== null) {
            config = { ...config, ...thinkingBudgetOrConfig };
        }
    }

    config = normalizeDisabledThinkingConfig(config);

    // Create a lean cache key that avoids stringifying large binary/base64 data
    const leanPrompt = Array.isArray(prompt)
        ? prompt.map(p => ({
            ...p,
            parts: p.parts.map(part => 'inlineData' in part ? { ...part, inlineData: { ...part.inlineData, data: '[REDACTED_FOR_CACHE_KEY]' } } : part)
        }))
        : typeof prompt === 'string' ? prompt : '[COMPLEX_OBJECT]';

    const cacheKeyString = JSON.stringify(leanPrompt) + JSON.stringify(schema || {}) + modelName;

    const cached = await aiCache.get(cacheKeyString, modelName, config);
    if (cached) {
        try {
            return extractAndParseJson<T>(cached, schema);
        } catch (__e: unknown) {
            // Ignore parse failure on stale cache
        }
    }

    const result = await ctx.rawGenerateContent(
        prompt,
        modelName,
        config,
        finalSystemInstruction
    );

    if (!result?.response) {
        throw new AppException(AppErrorCode.INTERNAL_ERROR, 'Intelligence Service returned an invalid response structure for structured data');
    }

    const text = typeof result.response.text === 'function' ? result.response.text() : '';
    if (!text) {
        throw new AppException(AppErrorCode.INTERNAL_ERROR, 'Intelligence Service returned an empty response for structured data');
    }
    await aiCache.set(cacheKeyString, text, modelName, config);

    return extractAndParseJson<T>(text, schema);
}

/**
 * Multi-turn chat.
 */
export async function chat(
    ctx: IntelligenceContext,
    history: ChatMessage[],
    newMessage: string,
    systemInstruction?: string
): Promise<string> {
    await ctx.ensureInitialized();
    const contents: Content[] = history.map(h => ({
        role: h.role,
        parts: h.parts as Part[] // Cast to satisfy firebase/ai types while preserving extra fields like thoughtSignature
    }));
    contents.push({ role: 'user', parts: [{ text: newMessage }] });

    const result = await ctx.rawGenerateContent(
        contents,
        ctx.model?.model,
        {},
        systemInstruction
    );

    return result.response.text();
}

/**
 * Analyze an image (base64).
 */
export async function analyzeImage(
    ctx: IntelligenceContext,
    prompt: string,
    imageBase64: string,
    mimeType: string = 'image/jpeg'
): Promise<string> {
    const base64Data = imageBase64.replace(/^data:image\/\w+;base64,/, "");
    const imagePart: Part = {
        inlineData: { data: base64Data, mimeType }
    };

    const result = await ctx.rawGenerateContent([{ role: 'user', parts: [{ text: prompt }, imagePart] }]);
    return result.response.text();
}

/**
 * Analyze generic parts (Video, Audio, PDF).
 */
export async function analyzeMultimodal(
    ctx: IntelligenceContext,
    prompt: string,
    parts: Part[]
): Promise<string> {
    const result = await ctx.rawGenerateContent([{ role: 'user', parts: [{ text: prompt }, ...parts] }]);
    return result.response.text();
}

/**
 * Analyze a file URI generated by the GeminiFileService (supports files up to 2GB).
 */
export async function analyzeFileURI(
    ctx: IntelligenceContext,
    fileUri: string,
    mimeType: string,
    prompt: string = 'Analyze this document in detail...'
): Promise<string> {
    const content: Content[] = [{
        role: 'user',
        parts: [
            { text: prompt },
            { fileData: { mimeType, fileUri } } as unknown as Part // Cast to bypass Firebase Autonomous strict part typing if needed
        ]
    }];
    const result = await ctx.rawGenerateContent(content);
    return result.response.text();
}

/**
 * Grounding with Google Search.
 */
export async function generateGroundedContent(
    ctx: IntelligenceContext,
    prompt: string,
    options?: { dynamicThreshold?: number }
): Promise<GenerateContentResult> {
    await ctx.ensureInitialized();
    const tools: Tool[] = [{
        googleSearch: {},
        googleSearchRetrieval: options?.dynamicThreshold ? {
            dynamicRetrievalConfig: {
                mode: 'MODE_DYNAMIC',
                dynamicThreshold: options.dynamicThreshold
            }
        } : undefined
    }] as unknown as Tool[];
    return ctx.rawGenerateContent(prompt, ctx.model!.model, {}, undefined, tools as unknown as Tool[]);
}

/**
 * Caption an image.
 */
export async function captionImage(
    ctx: IntelligenceContext,
    image: string | ArrayBuffer,
    prompt: string = 'Describe this image in detail...'
): Promise<string> {
    const base64 = typeof image === 'string' ? image : Buffer.from(image).toString('base64');
    const content: Content[] = [{
        role: 'user',
        parts: [
            { text: prompt },
            { inlineData: { mimeType: 'image/png', data: base64 } }
        ]
    }];
    const result = await ctx.rawGenerateContent(content);
    return result.response.text();
}

/**
 * Analyze audio content.
 */
export async function analyzeAudio(
    ctx: IntelligenceContext,
    audio: string | ArrayBuffer,
    prompt: string = 'Analyze this audio content in detail...'
): Promise<string> {
    const base64 = typeof audio === 'string' ? audio : Buffer.from(audio).toString('base64');
    const content: Content[] = [{
        role: 'user',
        parts: [
            { text: prompt },
            { inlineData: { mimeType: 'audio/mpeg', data: base64 } }
        ]
    }];
    const result = await ctx.rawGenerateContent(content);
    return result.response.text();
}

/**
 * Safely extracts and parses JSON from Gemini responses, handling markdown code fences,
 * commentary text, and validates against expected schema requirements.
 */
export function extractAndParseJson<T = Record<string, unknown>>(
    text: string,
    schema?: Schema | Record<string, unknown>
): T {
    if (!text || typeof text !== 'string') {
        logger.error('[HighLevelAPI] Empty response for structured JSON parsing');
        return undefined as unknown as T;
    }

    // 1. Try markdown code block fence extraction
    let candidate = text.trim();
    const codeBlockMatch = candidate.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
    if (codeBlockMatch) {
        candidate = codeBlockMatch[1].trim();
    } else {
        // Strip any dangling fences
        candidate = candidate.replace(/^```json\s*/i, '').replace(/\s*```$/i, '').trim();
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(candidate);
    } catch {
        // Fallback: extract outermost JSON object { ... } or array [ ... ]
        const firstBrace = candidate.indexOf('{');
        const lastBrace = candidate.lastIndexOf('}');
        if (firstBrace !== -1 && lastBrace > firstBrace) {
            try {
                parsed = JSON.parse(candidate.slice(firstBrace, lastBrace + 1));
            } catch {
                // Ignore fallback error
            }
        }
        if (parsed === undefined) {
            const firstBracket = candidate.indexOf('[');
            const lastBracket = candidate.lastIndexOf(']');
            if (firstBracket !== -1 && lastBracket > firstBracket) {
                try {
                    parsed = JSON.parse(candidate.slice(firstBracket, lastBracket + 1));
                } catch {
                    // Ignore fallback error
                }
            }
        }
    }

    if (parsed === undefined || parsed === null) {
        logger.error(`[HighLevelAPI] Failed to parse structured JSON from intelligence response: ${text.slice(0, 150)}`);
        return undefined as unknown as T;
    }

    // Schema validation: if schema specifies required properties, validate them
    if (schema && typeof schema === 'object' && 'required' in schema && Array.isArray(schema.required)) {
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            const parsedObj = parsed as Record<string, unknown>;
            const missing = (schema.required as string[]).filter(key => !(key in parsedObj));
            if (missing.length > 0) {
                logger.warn(`[HighLevelAPI] Structured JSON missing required schema keys: ${missing.join(', ')}`);
            }
        }
    }

    return parsed as T;
}

/**
 * Parse JSON from Intelligence response, handling markdown code blocks.
 */
export function parseJSON<T = Record<string, unknown>>(text: string | undefined): T | Record<string, never> {
    if (!text) return {};
    try {
        const parsed = extractAndParseJson<T>(text);
        return (parsed ?? {}) as T;
    } catch {
        logger.error('[HighLevelAPI] Failed to parse JSON:', text);
        return {} as T;
    }
}
